import { cloneDeep, find, get, set } from 'lodash-es';
import { STATE_MENU } from './EditSalesRoute.enum';
import { advancementStatusEnum, statusSalesRouteEnum } from '../../Services/SalesRoutes/enum';
import {
  getQuestionnaireSalesrouteByIdQuestionnaire,
  getQuotationSalesrouteByRefQuestionnaire,
} from '../../Services/SalesRoutes/utils';
import { updateMenuFromConfig } from './MenuConfig';
import { updateStep, updateMenuState } from './MenuConfig/menu.utils';

export const calculateSteps = ({ advancement, steps, salesroute = {}, idQuestionnaire, panel, nextEditBlockId }) => {
  try {
    if (!steps.length) {
      return steps;
    }

    // Need destructuring/new Array so that components get new data
    let menu = cloneDeep(steps);

    const advancementStatus = advancement.get('status');

    // get Questionnaire Salesroute By IdQuestionnaire
    const questionnaireSalesRoute = getQuestionnaireSalesrouteByIdQuestionnaire({
      salesroute,
      idQuestionnaire,
    });

    // get Quotation Salesroute By RefQuestionnaire
    const quotation = getQuotationSalesrouteByRefQuestionnaire({
      salesroute,
      refQuestionnaire: get(questionnaireSalesRoute, 'ref'),
    });

    // When request/success events
    switch (advancementStatus) {
      case advancementStatusEnum.EVENT_PATCH_SALESROUTE_REQUEST:
      case advancementStatusEnum.EVENT_RESET_FORM_SALESROUTE_REQUEST:
        return updateMenuState({ menu, common: { state: null, expanded: false } });
      case advancementStatusEnum.EVENT_ONHOLD_SUCCESS:
        // When ON HOLD, keep state but collapse all
        if (!panel) return updateMenuState({ menu, common: { state: null, expanded: false } });
        break;
      case advancementStatusEnum.EVENT_CLOSED_REQUEST:
      case advancementStatusEnum.EVENT_CLOSED_SUCCESS:
        return updateMenuState({ menu, common: { state: STATE_MENU.DISABLED, expanded: false } });
      case advancementStatusEnum.EVENT_OFFERS_BLOCK_REQUEST:
        return updateMenuState({
          menu,
          before: { state: STATE_MENU.DISABLED, expanded: false },
          current: { code: 'offers', state: STATE_MENU.CURRENT, expanded: true },
          after: { state: STATE_MENU.DISABLED, expanded: false },
        });
      case advancementStatusEnum.EVENT_FINALIZE_SALESROUTE_REQUEST:
        return updateMenuState({
          menu,
          before: { state: STATE_MENU.DISABLED, expanded: false },
          current: { code: 'documents', state: STATE_MENU.CURRENT, expanded: true },
        });
      default:
        break;
    }

    // When opening another block
    if (nextEditBlockId !== null && nextEditBlockId !== undefined) {
      menu = updateMenuFromConfig({
        salesroute,
        quotation,
        questionnaireSalesRoute,
        menu,
        onlyState: true,
      });

      return menu.map(step => {
        if (step.id === nextEditBlockId && step.state !== STATE_MENU.DISABLED) {
          return updateStep(step, STATE_MENU.CURRENT, true);
        }

        if (step.id !== nextEditBlockId && nextEditBlockId !== '__none__' && step.state === STATE_MENU.CURRENT) {
          return updateStep(step, STATE_MENU.ENABLED, false);
        }

        if (step.id !== nextEditBlockId && step.expanded) {
          return updateStep(step, null, false);
        }

        return step;
      });
    }

    // When clicking on menu
    if (panel) {
      menu = menu.map(step => {
        if (step.code !== panel.code) {
          const state = step.state === STATE_MENU.CURRENT ? STATE_MENU.ENABLED : step.state;
          return updateStep(step, state, false);
        }

        const state = step.state !== STATE_MENU.DISABLED ? panel.state || STATE_MENU.CURRENT : step.state;
        return updateStep(step, state, panel.expanded);
      });

      // If no current on menu (at loading), select and open the last item not disabled (so the last one enabled)
      if (!find(menu, { state: STATE_MENU.CURRENT })) {
        for (let i = menu.length - 1; i >= 0; i--) {
          if (menu[i].state !== STATE_MENU.DISABLED) {
            menu[i] = updateStep(menu[i], STATE_MENU.CURRENT, true);
            break;
          }
        }
      }

      return menu;
    }

    return updateMenuFromConfig({
      salesroute,
      quotation,
      questionnaireSalesRoute,
      menu,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`calculateSteps ${e.stack}`);
  }

  return steps;
};

/**
 * @typedef {Object} HeaderState
 * @property {string} statusSR - The header status
 * @property {Object} headerButtons - The states of the header buttons
 */
/**
 * @param  {string} status - The state of the current SalesRoute
 * @param   {Object} strings
 * @returns   {HeaderState}
 *            The HeaderState object {statusSR, headerButtons}
 */
export const calculateHeaderState = ({ status, strings }) => {
  let statusSR = 'play';
  let headerButtons = [
    {
      id: 0,
      label: get(strings, 'editsalesroute.action.button.putonhold.label', 'Mettre en attente'),
      type: 'warning',
      disabled: false,
    },
    {
      id: 1,
      label: get(strings, 'editsalesroute.action.button.cancel.label', 'Annuler'),
      type: 'error',
      disabled: false,
    },
  ];

  switch (status) {
    case statusSalesRouteEnum.ONHOLD:
      statusSR = 'pause';
      set(headerButtons, '[0].disabled', true);
      break;
    case statusSalesRouteEnum.CLOSED:
      statusSR = 'stop';
      set(headerButtons, '[0].disabled', true);
      set(headerButtons, '[1].disabled', true);
      break;
    case statusSalesRouteEnum.SUBSCRIBED:
      statusSR = 'play';
      set(headerButtons, '[0].disabled', true);
      set(headerButtons, '[1].disabled', true);
      break;
    default:
      break;
  }

  return { statusSR, headerButtons };
};
